/**
 * useCardDrawerEditing — local edit state + save pipeline for the kanban
 * CardDetailDrawer (title / summary / dates / status / priority / etc.).
 *
 * Extracted verbatim from CardDetailDrawer.tsx (operator 500-line ceiling).
 * Behaviour is unchanged — every state var, effect, dependency array and
 * handler is a 1:1 move. The two write handlers that previously lived AFTER
 * the drawer's `if (!open || !card) return null` early-return
 * (`patchCardSummary`, `handleMove`) gain an explicit `if (!card) return`
 * guard so they remain no-ops when called before a card resolves — the JSX
 * only ever invokes them once `card` is non-null, so the runtime contract
 * is identical.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import type { KanbanApi, KanbanMember } from '../../api'
import { KanbanCardConflictError, isKanbanNetworkError } from '../../api'
import { deriveMemberMentionIds } from '../../mentionTokens'
import type { Card, CardStatus } from '../../types'
import { operatorVisibleSummary } from './operatorVisibleSummary'

export interface UseCardDrawerEditingArgs {
  api: KanbanApi
  card: Card | null
  members?: KanbanMember[]
  open: boolean
  onClose: () => void
  pushFocusedCardUpdate: (updated: Card) => void
}

export function useCardDrawerEditing({
  api,
  card,
  members,
  open,
  onClose,
  pushFocusedCardUpdate,
}: UseCardDrawerEditingArgs) {
  // Local edit state — keep optimistic values until the patch
  // resolves so the inputs don't flicker between the user's typing
  // and the server's echo.
  const [title, setTitle] = useState(card?.title ?? '')
  const [summary, setSummary] = useState(() =>
    card ? operatorVisibleSummary(card) : '',
  )
  // Audit Arc 3 — drawer save protection.
  // ``savedAt`` is bumped to Date.now() on every successful patch
  // and drives the "Saved · 2s ago" pill next to the title.
  const [savedAt, setSavedAt] = useState<number | null>(null)
  // Per-field "is this in-flight?" markers, keyed by field name so the
  // close handler (and the explicit flush button) can ``Promise.allSettled``
  // them before letting the drawer disappear.
  const pendingFieldsRef = useRef<Map<string, Promise<unknown>>>(new Map())
  // Audit §3.6.2 (Arc 5) — track the last ``updated_at`` we've SEEN from the
  // server so the next PATCH or move can echo it as ``If-Unmodified-Since``.
  const lastSeenUpdatedAtRef = useRef<string | null>(card?.updated_at ?? null)
  // BUG-2026-06-29-KANBAN-DRAWER-EDIT-WIPED — only do the destructive reset
  // when the focused card IDENTITY changes or when there are no unsaved
  // local edits; a same-card refresh mid-edit only re-anchors the CAS ref.
  const lastInitCardIdRef = useRef<string | null>(card?.id ?? null)
  // Free-text fields the operator has touched but not yet committed.
  const dirtyFieldsRef = useRef<Set<'title' | 'summary' | 'blockedReason' | 'actionNote'>>(new Set())
  // FEAT-2026-06-29-KANBAN-ACTION-DATE — the operator's PRIMARY "next
  // action" date (scrum) + the free-text note that travels with it.
  const [actionDate, setActionDate] = useState<string>(card?.action_date ?? '')
  const [actionNote, setActionNote] = useState<string>(card?.action_note ?? '')
  const [dueDate, setDueDate] = useState<string>(card?.due_date ?? '')
  // PR 7 (Gantt timeline) — start_date drives the LEFT edge of the timeline.
  const [startDate, setStartDate] = useState<string>(card?.start_date ?? '')
  const [effortDays, setEffortDays] = useState<string>(
    card?.effort_days != null ? String(card.effort_days) : '',
  )
  const [priority, setPriority] = useState<number>(card?.priority ?? 1)
  const [status, setStatus] = useState<CardStatus>(card?.status ?? 'OPEN')
  // Status-context fields. Both writable + audited server-side.
  const [blockedReason, setBlockedReason] = useState<string>(card?.blocked_reason ?? '')
  const [snoozedUntil, setSnoozedUntil] = useState<string | null>(card?.snoozed_until ?? null)
  const [busyKey, setBusyKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Save resilience (Failed-to-fetch hardening). Remember HOW to re-run a
  // failed save so the operator gets Retry + we auto-resend on reconnect.
  const lastFailedSaveRef = useRef<(() => Promise<unknown>) | null>(null)
  const [hasFailedSave, setHasFailedSave] = useState(false)
  const [offlineSave, setOfflineSave] = useState(false)
  const clearFailedSave = useCallback(() => {
    lastFailedSaveRef.current = null
    setHasFailedSave(false)
    setOfflineSave(false)
  }, [])
  const retryLastSave = useCallback(() => {
    const thunk = lastFailedSaveRef.current
    if (!thunk) return
    setError(null)
    void thunk()
  }, [])
  const [logExpanded, setLogExpanded] = useState(false)
  const titleRef = useRef<HTMLTextAreaElement>(null)

  // Auto-grow the title field so a long card title is FULLY visible —
  // wrapping onto a second (or third) line instead of being clipped.
  useLayoutEffect(() => {
    const el = titleRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [title, open])

  // Reset local state whenever the focused card changes.
  useEffect(() => {
    if (!card) return
    // On a same-card refresh (lean→full fetch or a peer poll) keep any
    // free-text field the operator is mid-editing; a switch to a different
    // card starts with a clean slate.
    const isSameCard = lastInitCardIdRef.current === card.id
    const dirty = isSameCard ? dirtyFieldsRef.current : new Set()
    if (!dirty.has('title')) setTitle(card.title)
    if (!dirty.has('summary')) setSummary(operatorVisibleSummary(card))
    if (!dirty.has('blockedReason')) setBlockedReason(card.blocked_reason ?? '')
    // action_note is free-text: keep a mid-edit value on a same-card poll.
    if (!dirty.has('actionNote')) setActionNote(card.action_note ?? '')
    // Immediately-committed controls always adopt the server value.
    setActionDate(card.action_date ?? '')
    setDueDate(card.due_date ?? '')
    setStartDate(card.start_date ?? '')
    setEffortDays(card.effort_days != null ? String(card.effort_days) : '')
    setPriority(card.priority ?? 1)
    setStatus(card.status)
    setSnoozedUntil(card.snoozed_until ?? null)
    if (!isSameCard) {
      setError(null)
      clearFailedSave()
      dirtyFieldsRef.current = new Set()
    }
    lastInitCardIdRef.current = card.id
    // Arc 5 — re-anchor the CAS reference whenever the card prop changes.
    lastSeenUpdatedAtRef.current = card.updated_at ?? null
  }, [card, clearFailedSave])

  // Failed-to-fetch hardening — auto-resend the last failed save when the
  // browser reports it's back online. Only fires for network-level failures.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onOnline = () => {
      if (offlineSave && lastFailedSaveRef.current) {
        void lastFailedSaveRef.current()
      }
    }
    window.addEventListener('online', onOnline)
    return () => window.removeEventListener('online', onOnline)
  }, [offlineSave])

  // Audit Arc 3 — auto-clear the "Saved" pill 4 s after the last save.
  useEffect(() => {
    if (savedAt === null) return
    const t = window.setTimeout(() => setSavedAt(null), 4_000)
    return () => window.clearTimeout(t)
  }, [savedAt])

  // Audit Arc 3 — close-time flush + per-field patch tracking.
  const patchCard = useCallback(
    async (
      key: string,
      patch: Parameters<KanbanApi['updateCard']>[1],
    ) => {
      if (!card) return undefined
      setBusyKey(key)
      setError(null)
      // Arc 5 — pass the last-seen ``updated_at`` so the BE rejects (412) if
      // a peer already wrote since we opened the drawer.
      const promise = api.updateCard(card.id, patch, {
        expectedUpdatedAt: lastSeenUpdatedAtRef.current,
      })
      pendingFieldsRef.current.set(key, promise)
      try {
        const updated = await promise
        if (updated && (updated as Card).updated_at) {
          lastSeenUpdatedAtRef.current = (updated as Card).updated_at
        }
        pushFocusedCardUpdate(updated)
        setSavedAt(Date.now())
        clearFailedSave()
        return updated
      } catch (err) {
        if (err instanceof KanbanCardConflictError) {
          // Peer wrote since we opened the drawer. Refresh the local view
          // with the BE's truth so the operator can re-decide.
          if (err.current) {
            pushFocusedCardUpdate(err.current)
            lastSeenUpdatedAtRef.current = err.currentUpdatedAt
          }
          setError(
            'Card was updated by another user. Your changes were not saved — review and re-submit.',
          )
          clearFailedSave()
        } else {
          const networkish = isKanbanNetworkError(err)
          const status = (err as { status?: number } | null)?.status
          if (networkish) {
            setError(
              "Couldn't reach the server — your change is kept and will retry when you're back online.",
            )
          } else if (status === 404) {
            setError(
              'This card no longer exists on the server (it may not have finished creating). Reopen the board and try again.',
            )
          } else {
            setError(err instanceof Error ? err.message : 'Update failed')
          }
          lastFailedSaveRef.current = () => patchCard(key, patch)
          setHasFailedSave(true)
          setOfflineSave(networkish)
        }
        throw err
      } finally {
        setBusyKey(null)
        if (pendingFieldsRef.current.get(key) === promise) {
          pendingFieldsRef.current.delete(key)
        }
      }
    },
    [api, card, pushFocusedCardUpdate, clearFailedSave],
  )

  const closeWithFlush = useCallback(async () => {
    try {
      const flushes: Array<Promise<unknown>> = []
      if (card) {
        if (title.trim() && title.trim() !== card.title) {
          flushes.push(patchCard('title', { title: title.trim() }))
        }
        if (summary !== operatorVisibleSummary(card)) {
          flushes.push(
            patchCard('summary', {
              summary,
              summary_mentions: deriveMemberMentionIds(summary, members ?? []),
            }),
          )
        }
      }
      // Catch any in-flight saves that haven't resolved yet.
      for (const p of pendingFieldsRef.current.values()) {
        flushes.push(p)
      }
      if (flushes.length > 0) {
        await Promise.allSettled(flushes)
      }
    } finally {
      onClose()
    }
  }, [card, title, summary, patchCard, onClose, members])

  /**
   * Trello-aware description write. For Trello-mirrored cards the
   * description lives upstream — push to Trello and let the backend's
   * writeback service refresh the email_kanban Card row in-band. Native
   * cards fall back to the regular `patchCard` path.
   */
  const patchCardSummary = useCallback(
    async (newSummary: string) => {
      if (!card) return
      setBusyKey('summary')
      setError(null)
      try {
        if (
          card.provider === 'trello' &&
          card.external_id &&
          api.updateTrelloCard
        ) {
          await api.updateTrelloCard(card.external_id, { desc: newSummary })
          const optimistic = { ...card, summary: newSummary }
          pushFocusedCardUpdate(optimistic)
          clearFailedSave()
        } else {
          const summary_mentions = deriveMemberMentionIds(newSummary, members ?? [])
          await patchCard('summary', { summary: newSummary, summary_mentions })
        }
      } catch (err) {
        const networkish = isKanbanNetworkError(err)
        setError(
          networkish
            ? "Couldn't reach the server — your description is kept and will retry."
            : err instanceof Error
              ? err.message
              : 'Description save failed',
        )
        lastFailedSaveRef.current = () => patchCardSummary(newSummary)
        setHasFailedSave(true)
        setOfflineSave(networkish)
      } finally {
        setBusyKey(null)
      }
    },
    [api, card, members, patchCard, pushFocusedCardUpdate, clearFailedSave],
  )

  const handleMove = useCallback(
    async (laneId: string) => {
      if (!card) return
      if (laneId === card.lane) return
      setBusyKey('lane')
      setError(null)
      try {
        // Arc 5 — same CAS contract as patchCard so a peer's move can't be
        // silently clobbered by the drawer's lane switch.
        const updated = await api.moveCard(
          card.id,
          { lane_id: laneId, reason: 'detail_drawer' },
          { expectedUpdatedAt: lastSeenUpdatedAtRef.current },
        )
        if (updated && updated.updated_at) {
          lastSeenUpdatedAtRef.current = updated.updated_at
        }
        pushFocusedCardUpdate(updated)
        clearFailedSave()
      } catch (err) {
        if (err instanceof KanbanCardConflictError) {
          if (err.current) {
            pushFocusedCardUpdate(err.current)
            lastSeenUpdatedAtRef.current = err.currentUpdatedAt
          }
          setError(
            'Card was moved by another user. The drawer has been refreshed.',
          )
          clearFailedSave()
        } else {
          const networkish = isKanbanNetworkError(err)
          setError(
            networkish
              ? "Couldn't reach the server — the move will retry when you're back online."
              : err instanceof Error
                ? err.message
                : 'Move failed',
          )
          lastFailedSaveRef.current = () => handleMove(laneId)
          setHasFailedSave(true)
          setOfflineSave(networkish)
        }
      } finally {
        setBusyKey(null)
      }
    },
    [api, card, pushFocusedCardUpdate, clearFailedSave],
  )

  return {
    title, setTitle,
    summary, setSummary,
    savedAt,
    dirtyFieldsRef,
    actionDate, setActionDate,
    actionNote, setActionNote,
    dueDate, setDueDate,
    startDate, setStartDate,
    effortDays, setEffortDays,
    priority, setPriority,
    status, setStatus,
    blockedReason, setBlockedReason,
    snoozedUntil, setSnoozedUntil,
    busyKey,
    error,
    hasFailedSave,
    offlineSave,
    retryLastSave,
    logExpanded, setLogExpanded,
    titleRef,
    patchCard,
    closeWithFlush,
    patchCardSummary,
    handleMove,
  }
}
